import type { ReactElement } from "react"

/**
 * The OTT player frame draws its icons as Font Awesome 6 Pro glyphs, not as
 * exported assets — there is nothing to download, and FA Pro is not licensed for
 * this bundle. These are those glyphs' paths, on Font Awesome's own viewBox so
 * the proportions match the frame.
 *
 * All decorative: every one sits inside a control that carries its own
 * accessible name, which is why they are `aria-hidden` rather than titled.
 */

interface IconProps {
  /** Rendered size in px. Defaults to the frame's 16px inline icon. */
  readonly size?: number
}

interface GlyphProps extends IconProps {
  readonly viewBox: string
  /** One path, or several for a glyph built from repeated shapes. */
  readonly path: string | readonly string[]
}

const Glyph = ({ viewBox, path, size = 16 }: GlyphProps): ReactElement => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox={viewBox} fill="currentColor">
    {(typeof path === "string" ? [path] : path).map(d => (
      <path key={d} d={d} />
    ))}
  </svg>
)

/** `circle-play`, the light weight used for the centre control. */
export const PlayCircleIcon = ({ size }: IconProps): ReactElement => (
  <Glyph
    size={size}
    viewBox="0 0 512 512"
    path="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM188.3 147.1c-7.6 4.2-12.3 12.3-12.3 20.9l0 176c0 8.7 4.7 16.7 12.3 20.9s16.8 4.1 24.3-.5l144-88c7.1-4.4 11.5-12.1 11.5-20.4s-4.4-16.1-11.5-20.4l-144-88c-7.4-4.5-16.7-4.7-24.3-.5z"
  />
)

/** `circle-pause`, the light weight used for the centre control. */
export const PauseCircleIcon = ({ size }: IconProps): ReactElement => (
  <Glyph
    size={size}
    viewBox="0 0 512 512"
    path="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM224 192l0 128c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-128c0-8.8 7.2-16 16-16s16 7.2 16 16zm96 0l0 128c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-128c0-8.8 7.2-16 16-16s16 7.2 16 16z"
  />
)

/** `timer` — the playback-speed control, which the frame draws on a brand pill. */
export const TimerIcon = ({ size }: IconProps): ReactElement => (
  <Glyph
    size={size}
    viewBox="0 0 512 512"
    path="M176 0c-8.8 0-16 7.2-16 16s7.2 16 16 16l64 0 0 65c-45.6 3.5-87 22.5-118.7 51.7L98.6 125.4c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l23.4 23.4C71.4 205.3 54 250.6 54 300c0 111.6 90.4 202 202 202s202-90.4 202-202c0-49.4-17.4-94.7-46.4-130.2l25.7-25.7c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0l-25.3 25.3C357.7 119.4 314.9 99.6 272 97l0-65 64 0c8.8 0 16-7.2 16-16s-7.2-16-16-16L176 0zM86 300a170 170 0 1 1 340 0 170 170 0 1 1 -340 0zm186-96c0-8.8-7.2-16-16-16s-16 7.2-16 16l0 96c0 8.8 7.2 16 16 16s16-7.2 16-16l0-96z"
  />
)

/** `sliders` — the settings control. */
export const SlidersIcon = ({ size }: IconProps): ReactElement => (
  <Glyph
    size={size}
    viewBox="0 0 512 512"
    path="M0 96c0-8.8 7.2-16 16-16l112.9 0c7.1-27.6 32.2-48 62.1-48s55 20.4 62.1 48L496 80c8.8 0 16 7.2 16 16s-7.2 16-16 16l-242.9 0c-7.1 27.6-32.2 48-62.1 48s-55-20.4-62.1-48L16 112C7.2 112 0 104.8 0 96zM160 96a32 32 0 1 0 64 0 32 32 0 1 0 -64 0zM352 256a32 32 0 1 0 64 0 32 32 0 1 0 -64 0zm32-64c29.8 0 55 20.4 62.1 48l49.9 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-49.9 0c-7.1 27.6-32.2 48-62.1 48s-55-20.4-62.1-48L16 272c-8.8 0-16-7.2-16-16s7.2-16 16-16l305.9 0c7.1-27.6 32.2-48 62.1-48zM192 416a32 32 0 1 0 64 0 32 32 0 1 0 -64 0zm32-64c29.8 0 55 20.4 62.1 48L496 400c8.8 0 16 7.2 16 16s-7.2 16-16 16l-209.9 0c-7.1 27.6-32.2 48-62.1 48s-55-20.4-62.1-48L16 432c-8.8 0-16-7.2-16-16s7.2-16 16-16l145.9 0c7.1-27.6 32.2-48 62.1-48z"
  />
)

/** `closed-captioning`, outline weight — captions available but off. */
export const ClosedCaptionIcon = ({ size }: IconProps): ReactElement => (
  <Glyph
    size={size}
    viewBox="0 0 576 512"
    path="M512 96c17.7 0 32 14.3 32 32l0 256c0 17.7-14.3 32-32 32L64 416c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l448 0zM64 64C28.7 64 0 92.7 0 128L0 384c0 35.3 28.7 64 64 64l448 0c35.3 0 64-28.7 64-64l0-256c0-35.3-28.7-64-64-64L64 64zM200 176c14.1 0 27.3 5.5 37.3 15.4c6.3 6.2 16.4 6.2 22.6-.1s6.2-16.4-.1-22.6C243.7 152.5 222.7 144 200 144c-48.6 0-88 39.4-88 88l0 48c0 48.6 39.4 88 88 88c22.7 0 43.7-8.5 59.8-24.7c6.2-6.3 6.2-16.4-.1-22.6s-16.4-6.2-22.6 .1c-10 10-23.3 15.4-37.3 15.4c-30.9 0-56-25.1-56-56l0-48c0-30.9 25.1-56 56-56zm176 0c14.1 0 27.3 5.5 37.3 15.4c6.3 6.2 16.4 6.2 22.6-.1s6.2-16.4-.1-22.6C419.7 152.5 398.7 144 376 144c-48.6 0-88 39.4-88 88l0 48c0 48.6 39.4 88 88 88c22.7 0 43.7-8.5 59.8-24.7c6.2-6.3 6.2-16.4-.1-22.6s-16.4-6.2-22.6 .1c-10 10-23.3 15.4-37.3 15.4c-30.9 0-56-25.1-56-56l0-48c0-30.9 25.1-56 56-56z"
  />
)

/** `closed-captioning`, solid weight — captions on. */
export const ClosedCaptionOnIcon = ({ size }: IconProps): ReactElement => (
  <Glyph
    size={size}
    viewBox="0 0 576 512"
    path="M0 128C0 92.7 28.7 64 64 64l448 0c35.3 0 64 28.7 64 64l0 256c0 35.3-28.7 64-64 64L64 448c-35.3 0-64-28.7-64-64L0 128zM200 176c-30.9 0-56 25.1-56 56l0 48c0 30.9 25.1 56 56 56c14.1 0 27.3-5.5 37.3-15.4c6.3-6.2 16.4-6.2 22.6 .1s6.2 16.4-.1 22.6C243.7 359.5 222.7 368 200 368c-48.6 0-88-39.4-88-88l0-48c0-48.6 39.4-88 88-88c22.7 0 43.7 8.5 59.8 24.7c6.2 6.3 6.2 16.4-.1 22.6s-16.4 6.2-22.6-.1c-10-9.9-23.2-15.4-37.3-15.4zm176 0c-30.9 0-56 25.1-56 56l0 48c0 30.9 25.1 56 56 56c14.1 0 27.3-5.5 37.3-15.4c6.3-6.2 16.4-6.2 22.6 .1s6.2 16.4-.1 22.6C419.7 359.5 398.7 368 376 368c-48.6 0-88-39.4-88-88l0-48c0-48.6 39.4-88 88-88c22.7 0 43.7 8.5 59.8 24.7c6.2 6.3 6.2 16.4-.1 22.6s-16.4 6.2-22.6-.1c-10-9.9-23.2-15.4-37.3-15.4z"
  />
)

/** `check` — the selected row in the Playback Speed panel. */
export const CheckIcon = ({ size }: IconProps): ReactElement => (
  <Glyph
    size={size}
    viewBox="0 0 448 512"
    path="M443.3 100.7c6.2 6.2 6.2 16.4 0 22.6l-272 272c-6.2 6.2-16.4 6.2-22.6 0l-144-144c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L160 361.4 420.7 100.7c6.2-6.2 16.4-6.2 22.6 0z"
  />
)

/**
 * The session browser. Drawn rather than taken from Font Awesome: the rows
 * deliberately echo a session card — a thumbnail square beside its title — which
 * is what the panel it opens actually contains. A generic `list` glyph would say
 * less at the same size.
 */
export const SessionsIcon = ({ size }: IconProps): ReactElement => (
  <Glyph
    size={size}
    viewBox="0 0 512 512"
    path={[
      "M32 92h56v56H32z",
      "M136 100h344v40H136z",
      "M32 228h56v56H32z",
      "M136 236h344v40H136z",
      "M32 364h56v56H32z",
      "M136 372h344v40H136z",
    ]}
  />
)

/**
 * `arrow-left`. The OTT frame has no exit control — a TV remote has a Back
 * button and a browser does not — so this is an addition, not a translation.
 */
export const BackArrowIcon = ({ size }: IconProps): ReactElement => (
  <Glyph
    size={size}
    viewBox="0 0 448 512"
    path="M11.3 267.3c-6.2-6.2-6.2-16.4 0-22.6l160-160c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6L61.3 240 432 240c8.8 0 16 7.2 16 16s-7.2 16-16 16L61.3 272 193.9 404.7c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0l-160-160z"
  />
)
